import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useSyncExternalStore } from "react";
import { usePostHog } from "posthog-js/react";
import { useAuth } from "@/providers/useAuth";
import { watchlistsService } from "@/services/watchlistsService";
import type { WatchlistSecurityItem } from "@/services/watchlistsService";
import type { Watchlist } from "@/shared/types/watchlist";

export function unwrapResult<TData>(result: { ok: boolean; data?: TData; error?: string; cause?: unknown }): TData {
  if (!result.ok || result.data === undefined) {
    if (result.cause instanceof Error) {
      throw result.cause;
    }

    throw new Error(result.error ?? "Watchlists service operation failed.");
  }

  return result.data;
}

interface WatchlistsReadModel {
  readonly version: number;
  readonly data: Watchlist[];
  readonly isLoading: boolean;
  readonly lastError: string | undefined;
}

const EMPTY_READ_MODEL: WatchlistsReadModel = {
  version: -1,
  data: [],
  isLoading: false,
  lastError: undefined,
};

/**
 * Builds the hook's read model straight from the store, cached by `version` so
 * `useSyncExternalStore` returns a stable identity between emits (otherwise it
 * re-renders forever) and a fresh one on every emit.
 *
 * The data MUST flow through the `useSyncExternalStore` return value like this.
 * Reading `getWatchlists()`/`getState()` into separate consts (even behind a
 * `useMemo` keyed on version) lets the React Compiler treat the bare store call
 * as non-reactive and cache the first — usually empty — result for the life of
 * the component. That is exactly what left the Add-to-Watchlist dialog's list
 * stuck empty until a remount. Mirrors `WatchlistPillsCell`'s snapshot cache.
 */
function readWatchlists(cache: { current: WatchlistsReadModel | null }): WatchlistsReadModel {
  const version = watchlistsService.getVersion();
  const cached = cache.current;
  if (cached?.version === version) {
    return cached;
  }

  const state = watchlistsService.getState();
  const snapshot: WatchlistsReadModel = {
    version,
    data: watchlistsService.getWatchlists(),
    isLoading: state.meta.isLoading,
    lastError: state.meta.lastError,
  };
  cache.current = snapshot;
  return snapshot;
}

export function useWatchlists() {
  const { isAuthResolved, isAuthenticated } = useAuth();
  const cacheRef = useRef<WatchlistsReadModel | null>(null);
  const snapshot = useSyncExternalStore(
    // Store singleton method; it never reads `this`, so the unbound ref is safe.
    // oxlint-disable-next-line typescript/unbound-method
    watchlistsService.subscribe,
    () => readWatchlists(cacheRef),
    () => EMPTY_READ_MODEL,
  );

  // Guarantee the list is fetched the first time any consumer (a page or the
  // Add-to-Watchlist dialog) reads it, instead of relying solely on the one-shot
  // bootstrap. `ensureLoaded` dedupes in-flight calls and retries after failure,
  // so this is safe to fire on every mount — but only once the session is a
  // resolved, signed-in one. `AddToWatchlistDialogMount` calls this hook from
  // `AppProviders`, outside `AppBootstrapper`'s auth guard, so without this gate
  // the effect would request watchlists with no token: a 401 that fails silent
  // renewal clears the session and redirects.
  useEffect(() => {
    if (!isAuthResolved || !isAuthenticated) {
      return;
    }

    void watchlistsService.ensureLoaded();
  }, [isAuthResolved, isAuthenticated]);

  return {
    data: snapshot.data,
    isLoading: snapshot.isLoading,
    error: snapshot.lastError ? new Error(snapshot.lastError) : undefined,
    refetch: async () => unwrapResult(await watchlistsService.loadWatchlists()),
  } as const;
}

export function useCreateWatchlist() {
  const posthog = usePostHog();
  return useMutation({
    mutationFn: async (name: string) => unwrapResult(await watchlistsService.createWatchlist(name)),
    onSuccess: () => {
      posthog.capture("watchlist_created");
    },
  });
}

export function useRenameWatchlist() {
  const posthog = usePostHog();
  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) =>
      unwrapResult(await watchlistsService.renameWatchlist(id, name)),
    onSuccess: () => posthog.capture("watchlist_renamed"),
  });
}

export function useDeleteWatchlist() {
  const posthog = usePostHog();
  return useMutation({
    mutationFn: async (id: string) => {
      const result = await watchlistsService.deleteWatchlist(id);
      if (!result.ok) {
        throw new Error(result.error ?? "Watchlist delete failed.");
      }
    },
    onSuccess: () => {
      // Deleting the last watchlist makes the backend self-heal a fresh default
      // "My Watchlist" on the next list call. The store only removed the deleted
      // entry locally, so without this it would stay empty until a page reload.
      if (watchlistsService.getWatchlists().length === 0) {
        void watchlistsService.loadWatchlists();
      }
      posthog.capture("watchlist_deleted");
    },
  });
}

export function useAddSecuritiesToWatchlist() {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationFn: async ({ watchlistId, securityIdentifiers }: { watchlistId: string; securityIdentifiers: string[] }) =>
      unwrapResult(await watchlistsService.addSecurities(watchlistId, securityIdentifiers)),
    onSuccess: (_items: WatchlistSecurityItem[], variables) => {
      void queryClient.invalidateQueries({ queryKey: ["watchlists", variables.watchlistId, "securities"] });
      posthog.capture("watchlist_securities_added", { count: variables.securityIdentifiers.length });
    },
  });
}

export function useRemoveSecuritiesFromWatchlist() {
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return useMutation({
    mutationFn: async ({ watchlistId, securityIdentifiers }: { watchlistId: string; securityIdentifiers: string[] }) =>
      unwrapResult(await watchlistsService.removeSecurities(watchlistId, securityIdentifiers)),
    onMutate: async ({ watchlistId, securityIdentifiers }) => {
      // Prevent in-flight refetches from overwriting the optimistic removal
      await queryClient.cancelQueries({ queryKey: ["watchlists", watchlistId, "securities"] });

      // Snapshot all filter/page variants so we can roll back on error. Cached
      // entries are shaped as { items, totalCount } (useWatchlistBonds), not a
      // bare array — CTS-246: this used to assume a flat array, which threw in
      // onMutate and silently swallowed every remove before the API call fired.
      const previousData = queryClient.getQueriesData<{ items: WatchlistSecurityItem[]; totalCount: number }>({
        queryKey: ["watchlists", watchlistId, "securities"],
      });

      const removed = new Set(securityIdentifiers);
      queryClient.setQueriesData<{ items: WatchlistSecurityItem[]; totalCount: number }>(
        { queryKey: ["watchlists", watchlistId, "securities"] },
        (old) => {
          if (!old) return old;
          const items = old.items.filter((item) => !removed.has(item.securityIdentifier));
          return { ...old, items, totalCount: Math.max(0, old.totalCount - (old.items.length - items.length)) };
        },
      );

      return { previousData };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        for (const [queryKey, data] of context.previousData) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
    onSuccess: (_items, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["watchlists", variables.watchlistId, "securities"] });
      posthog.capture("watchlist_securities_removed", { count: variables.securityIdentifiers.length });
    },
  });
}

